import { InitialsAvatar } from "@/components/InitialsAvatar";
import type { Repo } from "@/types/repo";
import { ArrowSquareOut, GitFork, Star } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

interface Props {
  repo: Repo;
}

function formatNum(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(1) + "k";
  return String(n);
}

function formatLoc(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(1) + "k";
  return String(n);
}

export function RepoCard({ repo }: Props) {
  return (
    <article className="eth-card flex flex-col gap-3 p-5 h-full">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3
            className="font-semibold text-text-primary truncate"
            title={repo.full_name}
          >
            <Link
              to={`/repos/${repo.id}`}
              className="hover:text-primary transition-colors"
            >
              {repo.name}
            </Link>
          </h3>
          <span className="text-xs text-text-muted font-code">
            {repo.full_name}
          </span>
        </div>
        {repo.homepage_url && (
          <a
            href={repo.homepage_url}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-primary hover:text-primary-dark transition-colors"
            aria-label={`Open ${repo.name} live site`}
          >
            <ArrowSquareOut size={18} weight="bold" />
          </a>
        )}
      </div>

      {/* Description */}
      <p className="text-sm text-text-secondary leading-relaxed line-clamp-3 flex-1">
        {repo.description}
      </p>

      {/* Topics */}
      {repo.topics.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {repo.topics.slice(0, 4).map((t) => (
            <span
              key={t}
              className="text-[11px] px-2 py-0.5 rounded-full bg-primary-muted text-primary font-medium"
            >
              {t}
            </span>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-1 border-t border-border text-xs text-text-muted">
        {/* Left: language + stats */}
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: repo.language_color }}
            />
            {repo.language}
          </span>
          <span className="flex items-center gap-1">
            <Star
              size={12}
              weight="fill"
              className="text-warning"
            />
            {formatNum(repo.stars)}
          </span>
          <span className="flex items-center gap-1">
            <GitFork size={12} />
            {formatNum(repo.forks)}
          </span>
          {repo.lines_of_code > 0 && (
            <span>{formatLoc(repo.lines_of_code)} LOC</span>
          )}
        </div>

        {/* Right: contributors */}
        {repo.contributors.length > 0 && (
          <div className="flex items-center gap-1">
            <div className="flex -space-x-2">
              {repo.contributors.slice(0, 4).map((name) => (
                <InitialsAvatar key={name} name={name} size={22} />
              ))}
            </div>
            {repo.contributor_count > 4 && (
              <span className="text-[10px]">+{repo.contributor_count - 4}</span>
            )}
          </div>
        )}
      </div>

      {/* Release badge */}
      {repo.latest_release && (
        <div className="flex items-center justify-between">
          <span className="text-[11px] px-2 py-0.5 rounded bg-bg-surface text-text-muted font-code">
            {repo.latest_release}
          </span>
          {repo.license && (
            <span className="text-[10px] text-text-muted">
              {repo.license}
            </span>
          )}
        </div>
      )}
    </article>
  );
}
